import { Link } from "react-router-dom";
import { MapPin, Clock, DollarSign, Building2 } from "lucide-react";
import { getFullImageUrl } from "../../../utils/imageHandler";
import { Avatar, AvatarFallback } from "../avatar";

const JobCard = ({ job }) => {
  const isExpired = job.deadline && new Date(job.deadline) < new Date();

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out border border-gray-100 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 transform hover:-translate-y-1">
      <div className="flex justify-between items-start mb-4 gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Avatar className="w-12 h-12 rounded-xl border bg-white shadow-sm overflow-hidden">
            {(job.companyLogo || job.recruiter?.logoUrl) && (
              <img 
                src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)} 
                alt={job.companyName}
                className="w-full h-full object-contain p-1"
              />
            )}
            <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-black text-sm uppercase">
              {job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors truncate">
              <Link to={`/jobs/${job.id || job.jobId}`}>{job.title}</Link>
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 flex items-center gap-1 truncate">
              {job.companyName || job.recruiter?.companyName || "Unknown Company"}
            </p>
          </div>
        </div>
        {isExpired && (
          <span className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs px-2 py-1 rounded-full font-medium">
            Expired
          </span>
        )}
      </div>

      <div className="space-y-2 text-gray-600 dark:text-gray-300 text-sm mb-4">
        {job.location && (
          <p className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
            {job.location}
          </p>
        )}
        {job.salaryMin != null && job.salaryMax != null && (
          <p className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-500 dark:text-green-400" />
            {job.salaryMin.toLocaleString()} - {job.salaryMax.toLocaleString()}{" "}
            ETB
          </p>
        )}
        {job.deadline && (
          <p className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-500 dark:text-orange-400" />
            Deadline: {new Date(job.deadline).toLocaleDateString()}
          </p>
        )}
      </div>

      {job.tags && job.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {job.tags.slice(0, 3).map((tag, i) => (
            <span
              key={i}
              className="bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-xs font-medium px-3 py-1 rounded-full"
            >
              {tag}
            </span>
          ))}
          {job.tags.length > 3 && (
            <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-3 py-1 rounded-full">
              +{job.tags.length - 3} more
            </span>
          )}
        </div>
      )}

      <Link
        to={`/jobs/${job.id || job.jobId}`}
        className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
      >
        View Details
      </Link>
    </div>
  );
};

export default JobCard;
